import React, { ReactNode, useState } from 'react';
import { styled } from '@glitz/react';
import VideoProductsBlockViewModel from 'Shared/Blocks/Video/VideoProductsBlockViewModel.type';
import Modal, { ModalScrollableContent, ModalTabs, ModalTopContent } from 'Shared/SharedComponents/atoms/Modal';
import ArticleListItemViewModel from 'Shared/ViewModels/ContentListItemViewModel.type';
import MoreArticles from './MoreArticles';
import VideoContent from './VideoContent';
import VideoProducts from './VideoProducts';

const ModalScrollableContentMobile = styled(ModalScrollableContent, {
  paddingTop: '12px',
})

const ModalScrollableContentDesktop = styled(ModalScrollableContent, {
  paddingTop: '24px',
})

type EmbeddedContentLabels = {
  textAboveHeading: string;
  heading: string;
  close: string;
};

type PropType = {
  modalIsShown: boolean;
  hideModal: () => void;
  labels: EmbeddedContentLabels;
  mainContentLabel: string;
  mainContent: ReactNode;
  videoLabel: string;
  video?: VideoProductsBlockViewModel;
  videoProductsLabel: string;
  moreArticlesLabel: string;
  moreArticles?: ArticleListItemViewModel[];
  isCompact: boolean;
};

const EmbeddedContentModal = ({
  modalIsShown,
  hideModal,
  labels,
  mainContentLabel,
  mainContent,
  videoLabel,
  video,
  videoProductsLabel,
  moreArticlesLabel,
  moreArticles,
  isCompact,
}: PropType) => {
  enum Tabs {
    MainContent,
    Video,
    VideoProducts,
    MoreArticles,
  }

  const tabs = [{ tab: Tabs.MainContent, label: mainContentLabel }];

  if (isCompact && video?.videoSources && video.videoSources.length > 0) {
    tabs.push({ tab: Tabs.Video, label: videoLabel });
  }

  if (video?.productItems) {
    tabs.push({ tab: Tabs.VideoProducts, label: videoProductsLabel });
  }

  if(moreArticles && moreArticles.length > 0) {
    tabs.push({ tab: Tabs.MoreArticles, label: moreArticlesLabel });
  }
  
  const [selectedTab, setSelectedTab] = useState(Tabs.MainContent);

  const gotDesktopVideo =
    video?.videoSources !== null && video?.videoSources.findIndex((x) => x.device !== 'MOBILE') > -1;

  const ModalScrollable = isCompact ? ModalScrollableContentMobile : ModalScrollableContentDesktop;

  return (
    <Modal
      modalIsShown={modalIsShown}
      hideModal={hideModal}
      widthInColumns={6}
      paneContent={
        isCompact || !gotDesktopVideo ? undefined : (
          <VideoContent
            videoSources={video.videoSources}
            previewImage={video.imagePreview}
            label={video.label}
            heading={video.heading}
            playButtonText={video.playButtonText}
            isCompact={isCompact}
          />
        )
      }
    >
      <ModalTopContent
        textAboveHeading={labels.textAboveHeading}
        heading={labels.heading}
        closeText={isCompact ? undefined : labels.close}
        closeModal={hideModal}
      >
        <ModalTabs tabs={tabs} activeTab={selectedTab} switchToTab={setSelectedTab} />
      </ModalTopContent>
      {(selectedTab === Tabs.MainContent ||
        selectedTab === Tabs.VideoProducts ||
        selectedTab === Tabs.MoreArticles) && (
        <ModalScrollable>
          {selectedTab === Tabs.MainContent && mainContent}
          {selectedTab === Tabs.VideoProducts && video?.productItems && (
            <VideoProducts products={video.productItems} isCompact={isCompact} />
          )}
          {selectedTab === Tabs.MoreArticles && moreArticles && (
            <MoreArticles articles={moreArticles} isCompact={isCompact} />
          )}
        </ModalScrollable>
      )}
      {selectedTab === Tabs.Video && video?.videoSources?.length !== 0 && (
        <VideoContent
          videoSources={video.videoSources}
          previewImage={video.imagePreview}
          label={video.label}
          heading={video.heading}
          playButtonText={video.playButtonText}
          isCompact={isCompact}
        />
      )}
    </Modal>
  );
};

export default EmbeddedContentModal;
